import { action, computed, IObservableArray, observable } from "mobx";
import { UUID, DbRecording } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { DbRecordingEntryWriter } from "./DbRecordingEntryWriter";
import { RecordingCreated, RecordingDeleted, RecordingUpdated } from "../StateEvents";
import { EvidenceDatabase } from "./EvidenceDbStruct";
import { DbRecordingsTableReader } from "./DbRecordingsTableReader";
import { lazyObservable } from "mobx-utils";

class DbRecordingsCache {
	protected _cache: IObservableArray<DbRecording>;

	protected addOrUpdatedRecordingLocal = action( (recording: DbRecording) => {
		const originalLocation = this._cache.findIndex( e => e.recordingUuid === recording.recordingUuid);
		if (originalLocation === -1) { 
			this._cache.push(recording);
		} else {
			this._cache.replace([...this._cache.filter( e => e.recordingUuid !== recording.recordingUuid ), recording].sort( (a,b) => a.startTimeIso.localeCompare(b.startTimeIso) ));
		}
		// ProcessInfo.log(`Updated recordings table with recording ${recording.recordingUuid} at ${recording.startTimeIso}`, recording, toJS(this.recordings));
	});

	protected addOrUpdatedRecordingLocalByUuid = async (recordingUuid: UUID) => {
		const recording = await this.db.get("recordings", recordingUuid);
		if (recording != null) {
			this.addOrUpdatedRecordingLocal(recording);
		}
	}

	protected deleteRecordingLocal = action( (recordingUuid: UUID) => {
		this._cache.replace(this._cache.filter( e => e.recordingUuid != recordingUuid ));
	});

	protected constructor(public readonly db: EvidenceDatabase, initialRecordingsLoaded: DbRecording[]) {
		this._cache = observable.array(initialRecordingsLoaded);
		RecordingDeleted.addListener( async ({recordingUuid}) => this.deleteRecordingLocal(recordingUuid) );
	}

	@computed get recordings() {
		return this._cache;
	}

	@computed get recordingsInDescendingOrder() {
		return [...this.recordings].toSorted( (a,b) => b.startTimeIso.localeCompare(a.startTimeIso) );
	}

	@computed get recordingWritersNewestToOldest() {
		return this.recordingsInDescendingOrder.map( recording =>
			DbRecordingEntryWriter.fromDbAndRecording(this.db, recording)
		)
	}
}

export class DbRecordingsTableCached extends DbRecordingsCache {

	protected constructor(db: EvidenceDatabase, initialRecordingsLoaded: DbRecording[]) {
		super(db, initialRecordingsLoaded);
		RecordingCreated.addListener( ({recordingUuid}) => this.addOrUpdatedRecordingLocalByUuid(recordingUuid) );
		RecordingUpdated.addListener( ({recordingUuid}) => this.addOrUpdatedRecordingLocalByUuid(recordingUuid) );
	}

	static async create() {
		const [db, recordings] = await Promise.all([
			requireEvidenceDatabase(),
			DbRecordingsTableReader.getAllRecordings()
		]);
		return new DbRecordingsTableCached(db, recordings);
	}

	static #instancePromise: Promise<DbRecordingsTableCached> | undefined;
	static get instancePromise() {
		return DbRecordingsTableCached.#instancePromise ??= DbRecordingsTableCached.create();
	}
	@computed protected static get instanceLazyObservable() {
		return lazyObservable<DbRecordingsTableCached>( sink => DbRecordingsTableCached.instancePromise.then( instance => sink(instance)));
	}
	@computed static get instance() {
		return DbRecordingsTableCached.instanceLazyObservable.current();
	}
}

export class DbRecordingsInProgressCached extends DbRecordingsCache {

	#onRecordedAddedOrUpdated = async ({recordingUuid}: {recordingUuid: UUID}) => {
		const recording = await DbRecordingsTableReader.getRecording(recordingUuid);
		if (recording != null && recording.videoRecordingInProgress) {
			this.addOrUpdatedRecordingLocal(recording);
		}
		if (recording == null || !recording.videoRecordingInProgress) {
			this.deleteRecordingLocal(recordingUuid);
		}
	}

	protected constructor(db: EvidenceDatabase, initialRecordingsLoaded: DbRecording[]) {
		super(db, initialRecordingsLoaded);
		RecordingCreated.addListener( this.#onRecordedAddedOrUpdated );
		RecordingUpdated.addListener( this.#onRecordedAddedOrUpdated );
	}

	static async #create() {
		const [db, recordings] = await Promise.all([
			requireEvidenceDatabase(),
			DbRecordingsTableReader.getRecordingsInProgress()
		]);
		return new DbRecordingsInProgressCached(db, recordings);
	}

	static #instancePromise: Promise<DbRecordingsInProgressCached> | undefined;
	static get instancePromise() {
		return DbRecordingsInProgressCached.#instancePromise ??= DbRecordingsInProgressCached.#create();
	}
	@computed protected static get instanceLazyObservable() {
		return lazyObservable<DbRecordingsInProgressCached>( sink => DbRecordingsInProgressCached.instancePromise.then( instance => sink(instance)));
	}
	@computed static get instance() {
		return DbRecordingsInProgressCached.instanceLazyObservable.current();
	}


}
